// src/components/AudioRecorder.jsx
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Recorder from 'opus-recorder';
import { sendAudioAsAgent } from '../services/messageService';

/**
 * Graba una nota de voz y la envía al cliente.
 *
 * Usa `opus-recorder` (encoder OPUS en WebAssembly) en lugar del MediaRecorder
 * nativo para producir SIEMPRE un fichero OGG/OPUS, en todos los navegadores
 * (Chrome/Edge solo grabarían webm con MediaRecorder, y WhatsApp no lo acepta).
 * OGG/OPUS es el único formato que WhatsApp muestra como nota de voz real
 * (push-to-talk).
 *
 * El worker del encoder se sirve desde `public/opus/encoderWorker.min.js`.
 */
const ENCODER_PATH = `${process.env.PUBLIC_URL || ''}/opus/encoderWorker.min.js`;

export default function AudioRecorder({ conversationId, disabled = false }) {
  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0); // segundos
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const recorderRef = useRef(null);
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const closeRecorder = useCallback(() => {
    if (recorderRef.current) {
      try { recorderRef.current.close(); } catch { /* noop */ }
      recorderRef.current = null;
    }
  }, []);

  // Limpieza al desmontar.
  useEffect(() => {
    return () => {
      clearTimer();
      closeRecorder();
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [clearTimer, closeRecorder, audioUrl]);

  const resetState = useCallback(() => {
    clearTimer();
    closeRecorder();
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setModalOpen(false);
    setPreparing(false);
    setRecording(false);
    setElapsed(0);
    setAudioBlob(null);
    setAudioUrl(null);
    setError('');
  }, [audioUrl, clearTimer, closeRecorder]);

  const startRecording = async () => {
    if (disabled) return;

    // Abrir el modal de inmediato para dar feedback pase lo que pase.
    setError('');
    setAudioBlob(null);
    setAudioUrl(null);
    setModalOpen(true);

    if (!Recorder.isRecordingSupported()) {
      setError(
        t('audio.error.unsupported') ||
          'Tu navegador no soporta la grabación de audio.'
      );
      return;
    }

    setPreparing(true);

    try {
      const recorder = new Recorder({
        encoderPath: ENCODER_PATH,
        numberOfChannels: 1,
        encoderApplication: 2048, // OPUS_APPLICATION_AUDIO
      });
      recorderRef.current = recorder;

      recorder.ondataavailable = (typedArray) => {
        const blob = new Blob([typedArray], { type: 'audio/ogg' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
      };

      // Si el worker del encoder no carga / falla en runtime.
      recorder.onerror = (err) => {
        console.error('[AudioRecorder] recorder error:', err);
        clearTimer();
        setPreparing(false);
        setRecording(false);
        setError(t('audio.error.engine') || 'No se pudo iniciar el grabador de audio.');
      };

      await recorder.start();
      setPreparing(false);
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (err) {
      console.error('[AudioRecorder] start error:', err);
      clearTimer();
      setPreparing(false);
      setRecording(false);
      closeRecorder();
      // NotAllowedError / NotFoundError → permisos/micrófono; resto → motor.
      const isPermission = err && (err.name === 'NotAllowedError' || err.name === 'NotFoundError' || err.name === 'SecurityError');
      setError(
        isPermission
          ? (t('audio.error.permission') || 'No se pudo acceder al micrófono. Revisa los permisos del navegador.')
          : (t('audio.error.engine') || 'No se pudo iniciar el grabador de audio.')
      );
    }
  };

  const stopRecording = () => {
    clearTimer();
    setRecording(false);
    if (recorderRef.current) {
      // stop() vacía el encoder y dispara ondataavailable con el OGG final.
      try { recorderRef.current.stop(); } catch { /* noop */ }
    }
  };

  const handleSend = async () => {
    if (!audioBlob) return;
    try {
      setSending(true);
      setError('');
      await sendAudioAsAgent(conversationId, audioBlob, 'voice-note.ogg');
      resetState();
    } catch (err) {
      console.error('[AudioRecorder] Error sending audio:', err);
      setError(t('audio.error.send') || err.message || 'Error enviando la nota de voz');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const closeModal = () => {
    if (sending) return;
    resetState();
  };

  const previewModal = modalOpen && createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
      onClick={closeModal}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={closeModal}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-2xl"
          disabled={sending}
        >
          ✕
        </button>

        <h2 className="text-xl font-semibold mb-6">
          {t('audio.title') || 'Nota de voz'}
        </h2>

        {preparing && (
          <div className="flex flex-col items-center gap-3 mb-6 text-gray-600">
            <span className="inline-block w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
            <span>{t('audio.preparing') || 'Preparando micrófono...'}</span>
          </div>
        )}

        {recording && (
          <div className="flex flex-col items-center gap-4 mb-6">
            <div className="flex items-center gap-2 text-red-600">
              <span className="inline-block w-3 h-3 bg-red-600 rounded-full animate-pulse" />
              <span className="text-lg font-medium">{t('audio.recording') || 'Grabando...'}</span>
            </div>
            <p className="text-3xl font-mono text-gray-800">{formatTime(elapsed)}</p>
            <button
              onClick={stopRecording}
              className="mt-2 bg-red-600 text-white px-5 py-2 rounded-full hover:bg-red-700"
            >
              ⏹ {t('audio.stop') || 'Detener'}
            </button>
          </div>
        )}

        {!recording && !preparing && audioBlob && (
          <div className="flex flex-col gap-4 mb-6">
            <p className="text-sm text-gray-600">
              {t('audio.preview') || 'Escucha la grabación antes de enviarla:'}
            </p>
            {audioUrl && (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <audio controls src={audioUrl} className="w-full" />
            )}
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}

        {!recording && !preparing && audioBlob && (
          <div className="flex justify-between gap-3">
            <button
              onClick={resetState}
              className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100"
              disabled={sending}
            >
              {t('audio.discard') || 'Descartar'}
            </button>
            <button
              onClick={handleSend}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400"
              disabled={sending}
            >
              {sending ? (t('audio.sending') || 'Enviando...') : (t('audio.send') || 'Enviar nota de voz')}
            </button>
          </div>
        )}

        {/* Solo error (sin grabación ni preview): permitir cerrar/reintentar. */}
        {error && !recording && !preparing && !audioBlob && (
          <div className="flex justify-end">
            <button
              onClick={resetState}
              className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100"
            >
              {t('common.close') || 'Cerrar'}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );

  return (
    <>
      <button
        type="button"
        onClick={startRecording}
        disabled={disabled}
        title={
          disabled
            ? 'No puedes grabar notas de voz (esperando mensaje del cliente o conversación inactiva)'
            : (t('audio.record') || 'Grabar nota de voz')
        }
        className={`inline-block p-2 rounded transition-colors ${
          disabled
            ? 'text-gray-400 cursor-not-allowed opacity-50'
            : 'cursor-pointer text-gray-600 hover:text-blue-600 hover:bg-blue-50'
        }`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 11a7 7 0 01-14 0m7 7v3m0-3a4 4 0 01-4-4V7a4 4 0 118 0v4a4 4 0 01-4 4z"
          />
        </svg>
      </button>

      {previewModal}
    </>
  );
}
